import Chart from 'react-google-charts';
import { ChartSubType, InsightDashboardItemType } from '@g17eco/types/insight-custom-dashboard';
import { chartStyleNames, getChartOptions, toChartProps } from '@routes/summary/insights/utils/chartStyles';
import { ChartType, chartTooltipOptions } from '@routes/summary/insights/utils/constants';
import { BaseChartComponentProps, BaseHistoricalUtr, ChartDataProps, ChartDataResponse } from '../types';
import { TakenFromText } from './common/TakenFromText';
import { useChartData } from './useChartData';
import { hasChartDataRows, getUnfilledPortion, hasNegativeChartValues } from './multi-utrs-chart/utils';
import { UnsupportedWidget } from '@routes/custom-dashboard/items/charts/common/UnsupportedWidget';
import { CHART_SUBTYPE } from '@features/custom-dashboard/types';

type Props<T extends BaseHistoricalUtr> = BaseChartComponentProps<T> & {
  subType: ChartSubType.FullPie | ChartSubType.Pie | ChartSubType.PieWithLegend;
  getChartData: (props: ChartDataProps<T>) => Promise<ChartDataResponse>;
};

const CHART_SUBTYPE_STYLE_NAME_MAP = {
  [ChartSubType.FullPie]: chartStyleNames.chartStyleFullPie,
  [ChartSubType.PieWithLegend]: chartStyleNames.chartStylePieLegend,
  [ChartSubType.Pie]: '',
} as const;

export const PieChart = <T extends BaseHistoricalUtr>(props: Props<T>) => {
  const { utrsData, variables, calculation, subType, overrideOptions, getChartData } = props;
  const data = useChartData(getChartData, props);

  if (!utrsData.length || !variables || !calculation || !data || !hasChartDataRows(data.chartData)) {
    return null;
  }

  const { chartData, effectiveDate = '', period } = data;

  if (hasNegativeChartValues(chartData)) {
    return (
      <UnsupportedWidget
        item={{ ...props, type: InsightDashboardItemType.Chart, subType }}
        actionButton={null}
        unsupportedMessage={`${CHART_SUBTYPE[subType as keyof typeof CHART_SUBTYPE] ?? subType} cannot display negative values`}
      />
    );
  }

  const unfilledPortion = getUnfilledPortion<T>({ variables, utrsData, chartData });

  return (
    <>
      <div className='w-100 h-100' data-testid='pie-chart'>
        <Chart
          chartType={ChartType.PieChart}
          data={unfilledPortion ? [...chartData, unfilledPortion] : chartData}
          width={'100%'}
          height={'100%'}
          options={{
            ...getChartOptions({
              chartType: ChartType.PieChart,
              options: chartStyleNames[CHART_SUBTYPE_STYLE_NAME_MAP[subType]],
              chartProps: toChartProps(props),
              hasUnfilledPortion: !!unfilledPortion,
            }),
            tooltip: chartTooltipOptions,
            ...overrideOptions,
          }}
        />
      </div>
      <TakenFromText effectiveDate={effectiveDate} period={period} />
    </>
  );
};
